import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { SCHRIFTZUG } from "@/lib/media";
import { Button } from "@/components/ui/button";

export default function PaymentResult({ kind }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(kind === "success" ? "pending" : "cancelled");

  useEffect(() => {
    if (kind !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
  }, [kind, params]);

  const isCancel = kind === "cancel";

  return (
    <main data-testid="payment-result-page" className="min-h-screen flex items-center justify-center px-5 pt-20">
      <div className="max-w-md w-full text-center border border-white/10 bg-hell-coal p-10">
        <img src={SCHRIFTZUG} alt="Krampus Show" className="h-12 w-auto mx-auto mb-8" />
        {status === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-hell-gold animate-spin mx-auto mb-6" />
            <p data-testid="payment-pending" className="font-cinzel tracking-[0.2em] uppercase text-hell-bone/70">{t("payment_pending")}</p>
          </>
        )}
        {(status === "paid" || isCancel || status === "failed") && (
          <>
            {isCancel || status === "failed" ? (
              <XCircle className="w-12 h-12 text-hell-red mx-auto mb-6" />
            ) : (
              <CheckCircle2 className="w-12 h-12 text-hell-gold mx-auto mb-6" />
            )}
            <h1 data-testid="payment-result-title" className="font-display text-3xl text-hell-bone mb-4">
              {isCancel || status === "failed" ? t("payment_cancel_t") : t("payment_success_t")}
            </h1>
            <p className="text-hell-smoke text-sm mb-8">
              {isCancel || status === "failed" ? t("payment_cancel_d") : t("payment_success_d")}
            </p>
            <div className="flex flex-col gap-3">
              <Button data-testid="payment-back-tickets" onClick={() => navigate("/tickets")}
                className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs h-12">
                {t("back_tickets")}
              </Button>
              <Link to="/" data-testid="payment-back-home" className="font-cinzel text-xs tracking-[0.2em] uppercase text-hell-bone/60 hover:text-hell-gold transition-colors">
                {t("back_home")}
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
